import { rebased } from '../entity/rebase';
import { listOf, parseEntity } from '../entity/entity';
import type { VaultEntity } from '../entity/entity';
import { TAG_COLOR_IDS } from '../design/tagPalette';
import type { TagColorId } from '../design/tagPalette';

/**
 * The plane's two front-matter keys.
 *
 * **`plane:` — where things are.** Positions, and anything that is a property
 * of one placed card.
 * **`plane-links:` — what relates to what.** Connections between two of them.
 *
 * The split mirrors the app, which already keeps `PlaneLayoutBundle` and
 * `PlaneStructureBundle` apart for a stated reason: positions are "written
 * cheaply on every drag", structure "only on the discrete structural edits".
 * That reasoning is STRONGER here than in a database — a vault write
 * re-encrypts a file, bumps a shared manifest version and reaches every
 * device — so making a drag rewrite the connection list is exactly the churn
 * the split exists to avoid.
 *
 * Neither key is on `sources:`. That one is DERIVED, re-rendered from the
 * ArcMember edges on every export, so anything appended there is regenerated
 * away.
 *
 *     plane:
 *       - 2026/08/Recording.md @120,40 unlocked
 *       - 2026/08/Recording.md ^zetsbtsggdjyj @144,98
 *       - arcs/Member.md @460,120 ×320,200
 *       - 2026/09/Note 2026-09-23 at 21-40.md @80,300 green
 *     plane-links:
 *       - arcs/Member.md → 2026/08/Recording.md ^zetsbtsggdjyj
 *     plane-tints:
 *       - 2026/09/Note 2026-09-23 at 21-40.md orchid
 *
 * `plane-tints:` is a sticky's tag colour, keyed by the same ref as its
 * `plane:` line. The `plane:` line keeps its `warm`/`green` word either way —
 * an older build drops a line with any other word and then erases it on its
 * next canvas write — and a tint that resolves wins over the word. A card's
 * colour is its first tint line's, where that names a tag colour. A tint line
 * this head cannot read, or one whose ref is not placed, is kept as written.
 *
 * `@x,y` needs its sigil because a bare pair is indistinguishable from part
 * of a filename. `×w,h` is optional and unwritten today: `PlaneLayoutEntry`
 * models `widthDp`/`heightDp` already, so the format reserves room rather
 * than needing a second format change when the app starts using them.
 * `unlocked` rides the entry because a lock is a property of ONE cluster
 * rather than a relation, and that line is rewritten on drag anyway, so it
 * costs no extra write. A sticky's tint rides it for the same reason: it is
 * how one placed note is drawn, chosen per note.
 */

export interface PlaneRef {
  /** Vault path, as written. */
  ref: string;
  /** Topic anchors, when the card is a window into a body rather than a file. */
  anchors: string[];
}

export interface PlanePlacement extends PlaneRef {
  x: number;
  y: number;
  w?: number;
  h?: number;
  /** Members of this cluster move independently. Locked is the default —
   *  you cannot mess up an arrangement you have not made yet. */
  unlocked?: boolean;
  /** The card's height in lines (`~N`), rounded up to a half line. Absent: the kind's default. */
  lines?: number;
  /** The card is drawn open on this canvas (`open`). Absent: collapsed. */
  open?: boolean;
  /** How a placed note is drawn. Absent means the default sticky. */
  tint?: StickyTint;
  /** A tag colour chosen for a sticky, from `plane-tints:`. Wins over [tint]. */
  tagTint?: TagColorId;
  /** Words after the known ones that a newer app wrote, kept as written. */
  extra?: string;
}

export const STICKY_TINTS = ['warm', 'green'] as const;
export type StickyTint = (typeof STICKY_TINTS)[number];

export const isStickyTint = (t: string): t is StickyTint => (STICKY_TINTS as readonly string[]).includes(t);

/** [p] drawn in [tint]: a sticky word, or a tag colour — which keeps a word beside it, as the
 *  `plane:` line must. */
export function withTint(p: PlanePlacement, tint: StickyTint | TagColorId): PlanePlacement {
  const { tagTint: _, ...rest } = p;
  if (isStickyTint(tint)) return { ...rest, tint };
  return { ...rest, tint: p.tint ?? 'warm', tagTint: tint };
}

export interface PlaneLink {
  from: PlaneRef;
  to: PlaneRef;
}

const POSITION = /\s@(-?[\d.]+),(-?[\d.]+)((?:\s+\S+)*)\s*$/;
const SIZE_WORD = /^×([\d.]+),([\d.]+)$/;
const LINES_WORD = /^~(\d+(?:\.\d)?)$/;
const ARROW = /\s*(?:→|->)\s*/;

export function readPlane(entity: VaultEntity): PlanePlacement[] {
  const out: PlanePlacement[] = [];
  const tints = readTints(entity);
  for (const raw of listOf(entity.frontMatter['plane'])) {
    const placement = parsePlacement(raw);
    if (!placement) continue;
    const tagTint = tints.get(refKey(placement));
    out.push(tagTint ? { ...placement, tagTint } : placement);
  }
  return out;
}

function parsePlacement(raw: string): PlanePlacement | null {
  const at = raw.match(POSITION);
  if (!at) return null;
  const ref = parseRef(raw.slice(0, raw.length - at[0].length));
  const x = Number(at[1]);
  const y = Number(at[2]);
  // A position that is not two numbers is a line this head cannot read, kept as written.
  if (!ref || Number.isNaN(x) || Number.isNaN(y)) return null;
  const out: PlanePlacement = { ...ref, x, y };
  const extra: string[] = [];
  for (const word of at[3]!.trim().split(/\s+/).filter(Boolean)) {
    const size = SIZE_WORD.exec(word);
    const lines = LINES_WORD.exec(word);
    if (size && out.w === undefined && !Number.isNaN(Number(size[1])) && !Number.isNaN(Number(size[2]))) {
      out.w = Number(size[1]);
      out.h = Number(size[2]);
    } else if (lines && out.lines === undefined && Number(lines[1]) > 0) {
      out.lines = Number(lines[1]);
    } else if (word === 'open' && !out.open) out.open = true;
    else if (word === 'unlocked' && !out.unlocked) out.unlocked = true;
    else if ((word === 'warm' || word === 'green') && !out.tint) out.tint = word;
    else extra.push(word);
  }
  if (extra.length > 0) out.extra = extra.join(' ');
  return out;
}

/** The `plane-tints:` block as ref key → colour, each card's from its first line; one whose name
 *  is not a tag colour gives none, so the sticky falls back to its word. */
export function readTints(entity: VaultEntity): Map<string, TagColorId> {
  return firstTints(listOf(entity.frontMatter['plane-tints']));
}

export function readLinks(entity: VaultEntity): PlaneLink[] {
  return listOf(entity.frontMatter['plane-links']).flatMap((raw) => parseLink(raw) ?? []);
}

/** A line to nowhere is not a line: a half-written entry is not read rather than
 *  drawn as an edge with one end. */
function parseLink(raw: string): PlaneLink | null {
  const [left, right] = raw.split(ARROW);
  const from = parseRef(left ?? '');
  const to = parseRef(right ?? '');
  return from && to ? { from, to } : null;
}

export function writePlacement(p: PlanePlacement): string {
  const size = p.w !== undefined && p.h !== undefined ? ` ×${round(p.w)},${round(p.h)}` : '';
  return (
    `${refText(p)} @${round(p.x)},${round(p.y)}${size}` +
    `${p.unlocked ? ' unlocked' : ''}${p.tint ? ` ${p.tint}` : ''}` +
    // Last, so a reader that knows only the words before them keeps them as its extra.
    `${p.lines !== undefined ? ` ~${linesText(p.lines)}` : ''}${p.open ? ' open' : ''}${p.extra ? ` ${p.extra}` : ''}`
  );
}

/** Lines rounded up to the next half line, without a trailing `.0`. The app rounds with the same
 *  tolerance, so a height a hair over a half line writes the same `~N` on both. */
export function linesText(lines: number): string {
  return String(Math.ceil(lines * 2 - 1e-4) / 2);
}

export function writeLink(link: PlaneLink): string {
  return `${refText(link.from)} → ${refText(link.to)}`;
}

/** Writes `plane:` and `plane-tints:` together, so a card taken off the plane
 *  takes its tint line with it. */
export function withPlane(raw: string, placements: PlanePlacement[]): string {
  const lines = keepingUntouched(entriesOf(raw, 'plane'), placements, parsePlacement, refKey, writePlacement);
  return withBlock(withBlock(raw, 'plane', lines), 'plane-tints', tintsFor(raw, placements));
}

/**
 * The `plane-tints:` lines once [placements] are the plane, the way the app writes them: every line
 * kept as written — a colour this head does not know, a ref that is not placed — but those of a card
 * taken off, and those of a card whose colour the page set, changed or cleared, which go before its
 * new line is added (the app's `withTint`). A card's colour as both read it is its first line's.
 */
function tintsFor(raw: string, placements: readonly PlanePlacement[]): string[] {
  const placed = new Map(placements.map((p) => [refKey(p), p]));
  const before = new Set(entriesOf(raw, 'plane').flatMap((line) => {
    const p = parsePlacement(line);
    return p ? [refKey(p)] : [];
  }));
  const lines = entriesOf(raw, 'plane-tints');
  const read = firstTints(lines);
  const rewritten = new Set<string>();
  for (const key of before) if (!placed.has(key)) rewritten.add(key);
  for (const [key, card] of placed) if (card.tagTint !== read.get(key)) rewritten.add(key);
  const out = lines.filter((line) => {
    const tint = parseTint(line);
    return !tint || !rewritten.has(refKey(tint.ref));
  });
  for (const p of placements) {
    if (p.tagTint && rewritten.has(refKey(p))) out.push(`${refText(p)} ${p.tagTint}`);
  }
  return out;
}

/** Each card's colour as its first `plane-tints:` line gives it, where that names a tag colour. */
function firstTints(lines: readonly string[]): Map<string, TagColorId> {
  const first = new Map<string, string>();
  for (const line of lines) {
    const tint = parseTint(line);
    if (tint && !first.has(refKey(tint.ref))) first.set(refKey(tint.ref), tint.name);
  }
  const out = new Map<string, TagColorId>();
  for (const [key, name] of first) if ((TAG_COLOR_IDS as readonly string[]).includes(name)) out.set(key, name as TagColorId);
  return out;
}

function parseTint(line: string): { ref: PlaneRef; name: string } | null {
  const cut = line.trimEnd().lastIndexOf(' ');
  if (cut < 0) return null;
  const ref = parseRef(line.slice(0, cut));
  const name = line.slice(cut + 1).trim();
  return ref && name ? { ref, name } : null;
}

/**
 * [raw] with the card at [key] opened (drawn open, [lines] tall as measured) or closed (no `open`,
 * [lines] tall as its kind's collapsed estimate), and either way no longer loose: the card is the
 * person's now. With no [lines] the card carries no `~N`. A card that is not on the plane leaves the
 * file as it is.
 */
export function withCardOpen(raw: string, key: string, open: boolean, lines?: number): string {
  const placements = readPlane(parseEntity('', raw));
  const at = placements.findIndex((p) => refKey(p) === key);
  if (at < 0) return raw;
  const { open: _o, lines: _l, ...rest } = placements[at]!;
  const next = [...placements];
  next[at] = { ...rest, ...(open ? { open: true } : {}), ...(lines !== undefined ? { lines } : {}) };
  return withoutLoose(withPlane(raw, next), key);
}

/** [raw] without [key]'s line in `plane-loose:`; every other line, and a file with no such line, as written. */
export function withoutLoose(raw: string, key: string): string {
  const lines = entriesOf(raw, 'plane-loose');
  const kept = lines.filter((line) => {
    const ref = parseRef(line);
    return !ref || refKey(ref) !== key;
  });
  return kept.length === lines.length ? raw : withBlock(raw, 'plane-loose', kept);
}

export function withLinks(raw: string, links: PlaneLink[]): string {
  return withBlock(raw, 'plane-links', keepingUntouched(entriesOf(raw, 'plane-links'), links, parseLink, linkKey, writeLink));
}

/**
 * The block's lines for [wanted]: each line the file has, as written, where it says what is wanted
 * of it, its rewrite where that changed, and gone where it is not wanted; a line this head cannot
 * read stays where it was, since a newer app wrote it; then what is wanted and new. An older head
 * rewriting the whole block from what it read is what erased a newer app's lines.
 */
function keepingUntouched<T>(
  existing: string[],
  wanted: readonly T[],
  parse: (line: string) => T | null,
  key: (item: T) => string,
  write: (item: T) => string,
): string[] {
  const byKey = new Map(wanted.map((item) => [key(item), item]));
  const out: string[] = [];
  const written = new Set<string>();
  for (const line of existing) {
    const parsed = parse(line);
    if (!parsed) {
      out.push(line);
      continue;
    }
    const k = key(parsed);
    const want = byKey.get(k);
    if (!want || written.has(k)) continue;
    written.add(k);
    out.push(write(want) === write(parsed) ? line : write(want));
  }
  for (const item of wanted) {
    const k = key(item);
    if (!written.has(k)) {
      written.add(k);
      out.push(write(item));
    }
  }
  return out;
}

/** The entries of [key]'s block as the file holds them. */
export function entriesOf(raw: string, key: string): string[] {
  const block = new RegExp(`^${key}:\\n((?:[ \\t]+-[^\\n]*\\n)*)`, 'm').exec(raw);
  if (!block) return [];
  return block[1]!.split('\n').filter((l) => l.trim().startsWith('- ')).map((l) => l.trim().slice(2));
}

/** [after], the page's edit of [before], made again on the plane as the file
 *  holds it now — see [rebased]. */
export function placementsRebased(
  current: readonly PlanePlacement[],
  before: readonly PlanePlacement[],
  after: readonly PlanePlacement[],
): PlanePlacement[] {
  return rebased(current, before, after, refKey, (p) => `${writePlacement(p)}|${p.tagTint ?? ''}`);
}

/** The same for the plane's links. */
export function linksRebased(
  current: readonly PlaneLink[],
  before: readonly PlaneLink[],
  after: readonly PlaneLink[],
): PlaneLink[] {
  return rebased(current, before, after, linkKey, writeLink);
}

/** Same identity rule everywhere: a card is its path plus its anchors. */
export function refKey(ref: PlaneRef): string {
  return ref.anchors.length > 0 ? `${ref.ref}#${ref.anchors[0]}` : ref.ref;
}

/** A link's identity, the same way round it was written. */
export function linkKey(link: PlaneLink): string {
  return `${refKey(link.from)}→${refKey(link.to)}`;
}

/**
 * [links] with one from [from] to [to] added, or null when there is nothing to
 * add: a link to itself, or one the two already share, either way round.
 */
export function withLinkAdded(links: readonly PlaneLink[], from: PlaneRef, to: PlaneRef): PlaneLink[] | null {
  const a = refKey(from);
  const b = refKey(to);
  if (a === b) return null;
  const shared = links.some((l) => {
    const f = refKey(l.from);
    const t = refKey(l.to);
    return (f === a && t === b) || (f === b && t === a);
  });
  if (shared) return null;
  return [...links, { from: { ref: from.ref, anchors: from.anchors }, to: { ref: to.ref, anchors: to.anchors } }];
}

export function parseRef(text: string): PlaneRef | null {
  const [ref, ...anchors] = text.trim().split(' ^');
  if (!ref?.trim()) return null;
  return { ref: ref.trim(), anchors: anchors.map((a) => a.trim()).filter(Boolean) };
}

export function refText(ref: PlaneRef): string {
  return `${ref.ref}${ref.anchors.map((a) => ` ^${a}`).join('')}`;
}

function round(n: number): number {
  return Math.round(n);
}

/**
 * Rewrites one front-matter block. A targeted replace, so the body and the
 * base64 manifest survive byte for byte — the discipline every write here
 * follows.
 */
export function withBlock(raw: string, key: string, lines: string[]): string {
  const block = lines.length === 0 ? '' : `${key}:\n${lines.map((l) => `  - ${l}\n`).join('')}`;

  const existing = new RegExp(`^${key}:\\n(?:[ \\t]+-[^\\n]*\\n)*`, 'm');
  if (existing.test(raw)) return raw.replace(existing, block);
  if (!block) return raw;

  const fence = raw.indexOf('\n---\n', raw.indexOf('---\n') + 1);
  if (fence < 0) return raw;
  return `${raw.slice(0, fence + 1)}${block}${raw.slice(fence + 1)}`;
}
